/**
 * A v4 UUID that works in a NON-secure context.
 *
 * `crypto.randomUUID` is only defined over HTTPS or `localhost`. On a plain-http
 * LAN IP (e.g. testing at `http://10.10.1.11:5173`) it is `undefined` and calling
 * it throws — which would crash lead capture, the one thing that must never fail.
 * `crypto.getRandomValues` IS available in insecure contexts, so we fall back to
 * it and assemble the v4 layout by hand. In production the app is served over
 * HTTPS by the firewall, where the native `randomUUID` path is used.
 */
export function newUuid(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  // Set version (4) and variant (10xx) bits. `?? 0` keeps noUncheckedIndexedAccess
  // happy without a non-null assertion; the indices always exist on a 16-byte array.
  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40;
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;

  let hex = '';
  for (const byte of bytes) hex += byte.toString(16).padStart(2, '0');

  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}
